/**
 * aethereum serve — dumb room server.
 *
 * One room, one HTTP API, SSE broadcasting, authority enforcement.
 * No EventProcessor, no tmux, no agent lifecycle — those live client-side.
 * Humans connect via `aethereum join`, agents via `aethereum run claude`.
 */

import { createServer, type IncomingMessage, type ServerResponse } from "node:http";
import { spawn, execFileSync, type ChildProcess } from "node:child_process";
import { randomUUID } from "node:crypto";
import { createRequire } from "node:module";
import { tmpdir } from "node:os";
import { join as pathJoin } from "node:path";
import { readFileSync, writeFile } from "node:fs";

import { Room } from "../core/room.js";
import { FileBackedStorage } from "../core/storage.js";
import { HiveRoomState, type HiveSnapshot } from "../hive/room-state.js";
import { formatContractChangedPush, formatMessagePush } from "../hive/mcp.js";
import { createMirror, type PendingMirror } from "../hive/mirror.js";
import { HostedSubscriber } from "../hive/subscriber.js";
import { formatHostedPush } from "../hive/push.js";
import {
  BRAND,
  BRIEF_MAX_LEN,
  GOAL_MAX_LEN,
  VERIFICATION_MAX_LEN,
  idempotencyKey,
  searchEvents,
  Conformance,
  type Conformance as ConformanceType,
  type RoomEvent as HiveEvent,
} from "@br9704/aethereum-shared";

/** G1: validate an untyped request body against the conformance schema, fail-soft
 *  (a malformed spec is dropped rather than rejecting the whole declare). */
function parseConformance(v: unknown): ConformanceType | undefined {
  if (v == null) return undefined;
  const parsed = Conformance.safeParse(v);
  return parsed.success ? parsed.data : undefined;
}
import { randomRoomName, randomName } from "../core/names.js";
import { createEvent, type ActivityEvent, type AuthorityChangedEvent, type ParticipantKickedEvent, type RoomEvent } from "../core/events.js";
import type { AuthorityLevel } from "../core/types.js";
import type { Channel } from "../core/channel.js";
import { formatTimestamp } from "../agent/prompts.js";
import { TokenManager, buildShareUrl } from "./auth.js";

// ── Types ─────────────────────────────────────────────────────────────────────

interface ConnectedParticipant {
  id: string;
  name: string;
  authority: AuthorityLevel;
  channel: Channel;
  sessionToken: string;
  /**
   * STABLE hive member identity, distinct from the ephemeral connection `id`.
   * `id` is minted fresh per /join (per connection); the hive cursor, directed
   * messages, claims and intent must key on an identity that SURVIVES a
   * reconnect (network flap, session restart), or every rejoin resets the cursor
   * to 0 (re-flooding historical alerts) and orphans directed messages addressed
   * to the old id. Derived from the join handle so the same agent rejoining is
   * recognised as the same member. See `memberIdForHandle`.
   */
  hiveMemberId: string;
}

interface ConnectedGuest {
  id: string;
  authority: "guest";
  channel: Channel;
  sessionToken: string;
}

export interface ServeOptions {
  room?: string;
  port?: number;
  share?: boolean;
  quiet?: boolean;
  /** Suppress all human-readable output; emit one JSON line with server info on stdout. */
  headless?: boolean;
  /** Path to save room state to (JSON file, written on every event). */
  save?: string;
  /** Path to load room state from (JSON file). Implies save to the same file. */
  load?: string;
}

export interface ServeResult {
  serverUrl: string;
  publicUrl: string;
  roomName: string;
  adminToken: string;
  memberToken: string;
  /** Close the server without exiting the process (used by embedders + tests). */
  stop: () => Promise<void>;
}

// ── SSE helper ───────────────────────────────────────────────────────────────

async function enrichAndSend(res: ServerResponse, event: RoomEvent, room: Room): Promise<void> {
  if (event.type === "MessageSent" && event.message.reply_to_id) {
    const replyMsg = await room.getMessage(event.message.reply_to_id);
    const enriched = {
      ...event,
      _replyToName: replyMsg?.sender_name ?? null,
    };
    res.write(`data: ${JSON.stringify(enriched)}\n\n`);
    return;
  }
  res.write(`data: ${JSON.stringify(event)}\n\n`);
}

// ── Main serve command ───────────────────────────────────────────────────────

export async function serve(options: ServeOptions): Promise<ServeResult> {
  const roomName = options.room ?? randomRoomName();
  const port = options.port ?? 7890;
  const serverUrl = `http://127.0.0.1:${port}`;
  const log = options.headless ? () => {} : logServer;

  let publicUrl = serverUrl;
  let tunnelProcess: ChildProcess | null = null;

  // Create room with persistence (default: tmp folder)
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19); // YYYY-MM-DDTHH-MM-SS
  const savePath = options.save ?? options.load ?? pathJoin(tmpdir(), `hive-${roomName}-${timestamp}.json`);
  let storage;
  if (options.load) {
    try {
      storage = await FileBackedStorage.load(options.load);
      log(`loaded room state from ${options.load}`);
    } catch (err: any) {
      if (err.code === "ENOENT") {
        storage = new FileBackedStorage(options.load);
        log(`no existing file at ${options.load}, starting fresh`);
      } else {
        throw err;
      }
    }
  } else {
    storage = new FileBackedStorage(savePath);
  }
  const room = new Room(roomName, storage);

  // ── Durable hive state (bug B1) ─────────────────────────────────────────
  // The structured-payload layer is otherwise pure in-memory: a crash/redeploy
  // would wipe every contract/decision/ticket/goal/cursor and reset the mirror
  // outbox. We persist a SIDECAR next to the chat save file and rehydrate it on
  // boot (best-effort; a missing/corrupt sidecar just starts fresh).
  const hiveSidecarPath = `${savePath}.hive.json`;
  interface HiveSidecar {
    v: 1;
    hive: HiveSnapshot;
    handleToMember: Array<[string, string]>;
    mirror: PendingMirror[];
  }
  let loadedSidecar: HiveSidecar | null = null;
  try {
    loadedSidecar = JSON.parse(readFileSync(hiveSidecarPath, "utf-8")) as HiveSidecar;
    log(`loaded hive state from ${hiveSidecarPath}`);
  } catch {
    loadedSidecar = null; // no sidecar yet, or unreadable — start fresh
  }

  // Aethereum structured-payload layer (intents / versioned contracts / presence).
  // Chat stays in the forked substrate above; structured context is served via
  // the /hive/* endpoints below and consumed by the Aethereum MCP tools.
  const hive = HiveRoomState.fromJSON(loadedSidecar?.hive);
  // Best-effort mirror of structured events to the hosted sync API (→ Supabase
  // → dashboard). No-op unless AETHEREUM_SYNC_API_URL + AETHEREUM_SYNC_TOKEN are set.
  const mirror = createMirror();

  // Debounced sidecar writer: persists the reducer snapshot + stable-identity
  // map + the mirror's undelivered outbox. Best-effort and never throws into the
  // request path. onChange from the mirror (delivery progress) also triggers it.
  let persistTimer: ReturnType<typeof setTimeout> | null = null;
  function persistHive(): void {
    if (persistTimer) return;
    persistTimer = setTimeout(() => {
      persistTimer = null;
      const data: HiveSidecar = {
        v: 1,
        hive: hive.toJSON(),
        handleToMember: [...handleToMember],
        mirror: mirror.pending(),
      };
      writeFile(hiveSidecarPath, JSON.stringify(data), () => {});
    }, 250);
    (persistTimer as unknown as { unref?: () => void }).unref?.();
  }
  mirror.onChange = () => persistHive();

  // ── Cross-machine sync (the hosted spine, both directions) ──────────────
  // Up: every mirrored event's idempotency key is remembered so it can be
  // recognized when it echoes back over the stream. Down: a HostedSubscriber
  // merges OTHER machines' events into local state via ingestRemote and pushes
  // one-liners into live sessions through a relay participant. Silent degrade
  // throughout — sync loss never affects the local room.
  //
  // Echo-dedup (bug B12): a time-windowed guard, not a count-bounded Set. The
  // old 2000-entry FIFO could evict a key before its echo returned under burst,
  // re-ingesting our own event as foreign. Echo round-trips are seconds, so a
  // generous time window is correct regardless of throughput; memory is bounded
  // by events-per-window (small for a coordination room).
  const ECHO_WINDOW_MS = 10 * 60_000;
  const sentKeys = new Map<string, number>();
  function markSent(key: string): void {
    const now = Date.now();
    sentKeys.set(key, now);
    if (sentKeys.size > 256) {
      for (const [k, t] of sentKeys) if (now - t > ECHO_WINDOW_MS) sentKeys.delete(k);
    }
  }
  function wasSent(key: string): boolean {
    const t = sentKeys.get(key);
    return t !== undefined && Date.now() - t <= ECHO_WINDOW_MS;
  }
  const mirrorTracked = (event: HiveEvent) => {
    if (!mirror.enabled) return;
    markSent(idempotencyKey(room.roomId, event));
    mirror.send(room.roomId, event);
    persistHive();
  };

  let spineSubscriber: HostedSubscriber | null = null;
  let relayChannel: Channel | null = null;
  if (mirror.enabled) {
    relayChannel = await room.connect("__hive_relay__", "hive", {
      type: "agent",
      silent: true,
    });
    spineSubscriber = new HostedSubscriber({
      baseUrl: process.env.AETHEREUM_SYNC_API_URL!,
      token: process.env.AETHEREUM_SYNC_TOKEN!,
      includeOwn: true, // room-scope token has no member; filtering is by key
      onEvent: ({ event, id, handle }) => {
        if (wasSent(idempotencyKey(room.roomId, event))) return; // our own echo
        const result = hive.ingestRemote(event);
        if (!result.applied || !relayChannel) return;
        // push to live sessions via the chat substrate (SSE → tmux bridge)
        const line = formatHostedPush({ event, id, handle });
        if (line) void relayChannel.sendMessage(line).catch(() => {});
        if (result.changed) {
          void relayChannel.sendMessage(formatContractChangedPush(result.changed)).catch(() => {});
        }
      },
    });
    void spineSubscriber.start();
    log(`cross-machine sync: subscribed to the hosted spine`);
  }

  // Auth
  const tokens = new TokenManager();

  // Connected participants and guests (by session token for lookup)
  const participants = new Map<string, ConnectedParticipant>();
  const guests = new Map<string, ConnectedGuest>();
  // Reverse lookup: participantId → sessionToken
  const idToSession = new Map<string, string>();

  // Stable hive identity: a join handle → a persistent `member_<id>`. Keyed by a
  // normalised handle so the same agent (same machine/name) rejoining reuses its
  // member id — and therefore its cursor and directed-message inbox — instead of
  // getting a fresh ephemeral id each connection (the B2 re-flood/drop bug). This
  // map is persisted with the room state (see the DurableLog save/rehydrate).
  const handleToMember = new Map<string, string>(loadedSidecar?.handleToMember ?? []);
  function memberIdForHandle(handle: string): string {
    const key = handle.trim().toLowerCase() || "agent";
    let id = handleToMember.get(key);
    if (!id) {
      id = `member_${randomUUID().slice(0, 8)}`;
      handleToMember.set(key, id);
      persistHive(); // a new stable identity must survive a restart
    }
    return id;
  }

  // Replay any undelivered mirror events persisted before a restart (bug B11).
  // Safe here: `handleToMember` is initialised, so the onChange→persist path has
  // everything it closes over.
  if (loadedSidecar?.mirror?.length) mirror.hydrate(loadedSidecar.mirror);

  // Track active SSE connections for cleanup
  const sseConnections = new Map<string, ServerResponse>();

  // ── JSON body parser helper ──────────────────────────────────────────────

  async function parseBody(req: IncomingMessage): Promise<Record<string, unknown>> {
    const chunks: Buffer[] = [];
    for await (const chunk of req) chunks.push(chunk as Buffer);
    try { return JSON.parse(Buffer.concat(chunks).toString()); } catch { return {}; }
  }

  // ── Auth helper ──────────────────────────────────────────────────────────

  function getSession(token: string | null) {
    if (!token) return null;
    const p = participants.get(token);
    if (p) return { ...p, kind: "participant" as const };
    const g = guests.get(token);
    if (g) return { ...g, kind: "guest" as const };
    return null;
  }

  /**
   * The STABLE hive identity for a session. Participants carry `hiveMemberId`
   * (survives reconnect); anything else (a guest, which is blocked from hive
   * writes anyway) falls back to the connection id. All `/hive/*` handlers key
   * on this, never on the ephemeral connection `id`.
   */
  function hiveMemberOf(session: { id: string; hiveMemberId?: string }): string {
    return session.hiveMemberId ?? session.id;
  }

  function jsonError(res: ServerResponse, status: number, error: string): void {
    res.writeHead(status, { "Content-Type": "application/json" });
    res.end(JSON.stringify({ error }));
  }

  function jsonOk(res: ServerResponse, data: Record<string, unknown> = {}): void {
    res.writeHead(200, { "Content-Type": "application/json" });
    res.end(JSON.stringify({ ok: true, ...data }));
  }

  // ── HTTP API ────────────────────────────────────────────────────────────

  const httpServer = createServer(async (req, res) => {
    const url = new URL(req.url ?? "/", `http://localhost:${port}`);

    // ── SSE event stream ───────────────────────────────────────────────────
    // ⚠️  MUST accept POST — DO NOT change to GET-only.
    // Cloudflare Quick Tunnels buffer GET streaming responses and only flush
    // when the connection closes. POST streams in real-time. (cloudflared#1449)
    // https://github.com/cloudflare/cloudflared/issues/1449
    if (url.pathname === "/events" && (req.method === "GET" || req.method === "POST")) {
      const authHeader = req.headers.authorization;
      const sessionToken = authHeader?.startsWith("Bearer ")
        ? authHeader.slice(7)
        : null;
      const session = getSession(sessionToken);

      if (!session) {
        jsonError(res, 401, "Invalid session token");
        return;
      }

      // SSE headers
      res.writeHead(200, {
        "Content-Type": "text/event-stream",
        "Cache-Control": "no-cache",
        "Connection": "keep-alive",
        "Access-Control-Allow-Origin": "*",
      });
      res.flushHeaders();

      // Disable Nagle's algorithm so SSE events flush immediately.
      // Without this, small writes may be delayed up to ~200ms waiting
      // for more data, which can cause events to appear "stuck" until
      // the next event (e.g. a MentionedEvent) pushes the buffer.
      res.socket?.setNoDelay(true);

      sseConnections.set(session.id, res);

      // Send recent history so the joiner has context
      const history = await room.listEvents(undefined, 50);
      for (const event of [...history.items].reverse()) {
        await enrichAndSend(res, event, room);
      }

      // Live event stream
      const streamEvents = async () => {
        try {
          for await (const event of session.channel) {
            await enrichAndSend(res, event, room);
          }
        } catch {
          // Channel disconnected
        }
      };
      streamEvents();

      // Cleanup on client disconnect
      req.on("close", () => {
        sseConnections.delete(session.id);
      });
      return;
    }

    // ── GET endpoints ─────────────────────────────────────────────────────

    if (req.method === "GET") {
      const sessionToken = url.searchParams.get("token");
      const session = getSession(sessionToken);

      // ── GET /participants ────────────────────────────────────────────────
      if (url.pathname === "/participants") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        const list = room.listParticipants().map((p) => ({
          id: p.id,
          name: p.name,
          type: p.type,
          authority: p.authority ?? "member",
        }));
        res.writeHead(200, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ participants: list }));
        return;
      }

      // ── GET /message/:id ─────────────────────────────────────────────────
      if (url.pathname.startsWith("/message/")) {
        if (!session) return jsonError(res, 401, "Invalid session token");
        const messageId = url.pathname.slice("/message/".length);
        const msg = await room.getMessage(messageId);
        if (!msg) return jsonError(res, 404, "Message not found");
        res.writeHead(200, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ message: msg }));
        return;
      }

      // ── GET /messages ────────────────────────────────────────────────────
      if (url.pathname === "/messages") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        const count = parseInt(url.searchParams.get("count") ?? "30", 10);
        const cursor = url.searchParams.get("cursor") ?? null;
        const result = await room.listMessages(count, cursor);
        res.writeHead(200, { "Content-Type": "application/json" });
        res.end(JSON.stringify(result));
        return;
      }

      // ── GET /events/history ──────────────────────────────────────────────
      if (url.pathname === "/events/history") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        const category = url.searchParams.get("category") ?? null;
        const count = parseInt(url.searchParams.get("count") ?? "50", 10);
        const cursor = url.searchParams.get("cursor") ?? null;
        const result = await room.listEvents(category as any, count, cursor);
        res.writeHead(200, { "Content-Type": "application/json" });
        res.end(JSON.stringify(result));
        return;
      }

      // ── GET /search ──────────────────────────────────────────────────────
      if (url.pathname === "/search") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        const query = url.searchParams.get("query") ?? "";
        if (!query) return jsonError(res, 400, "Missing query parameter");
        const count = parseInt(url.searchParams.get("count") ?? "10", 10);
        const cursor = url.searchParams.get("cursor") ?? null;
        const result = await room.searchMessages(query, count, cursor);
        res.writeHead(200, { "Content-Type": "application/json" });
        res.end(JSON.stringify(result));
        return;
      }

      // ── GET /hive/context — the get_team_context payload ──────────────────
      // ?peek=1 → read-only, member-scoped snapshot: real alerts, but the cursor
      // does NOT advance, so the awareness footer, MCP resources, and hooks can
      // read without eating a delivery the explicit get_team_context should see.
      if (url.pathname === "/hive/context") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        const ctx = url.searchParams.get("peek")
          ? hive.getContext(hiveMemberOf(session), { consume: false })
          : hive.getContext(hiveMemberOf(session));
        res.writeHead(200, { "Content-Type": "application/json" });
        res.end(JSON.stringify(ctx));
        return;
      }

      // ── GET /hive/search — read-only memory recall over the room's events ─
      if (url.pathname === "/hive/search") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        const q = url.searchParams.get("q") ?? "";
        const limRaw = Number(url.searchParams.get("limit"));
        const limit = Number.isFinite(limRaw) && limRaw > 0 ? Math.min(Math.floor(limRaw), 50) : 8;
        const results = searchEvents(hive.eventsSince(0), q, limit);
        res.writeHead(200, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ results }));
        return;
      }
    }

    // ── POST endpoints ────────────────────────────────────────────────────

    if (req.method === "POST") {
      const body = await parseBody(req);

      // ── POST /join ──────────────────────────────────────────────────────
      if (url.pathname === "/join") {
        // Accept share token OR legacy type-based join
        const shareToken = String(body.token ?? "");
        const legacyType = String(body.type ?? "");

        let authority: AuthorityLevel;

        if (shareToken) {
          const tokenAuthority = tokens.validateShareToken(shareToken);
          if (!tokenAuthority) return jsonError(res, 403, "Invalid share token");
          authority = tokenAuthority;
        } else if (legacyType === "guest") {
          authority = "guest";
        } else if (legacyType === "human") {
          authority = "member";
        } else {
          // Default: agent joins as member
          authority = "member";
        }

        const participantType = String(body.type ?? "human") as "human" | "agent";
        const name = String(body.name ?? randomName());

        if (authority === "guest") {
          const id = `obs_${randomUUID().slice(0, 8)}`;
          const channel = room.observe();
          const sessionToken = tokens.createSessionToken(id, "guest");

          guests.set(sessionToken, { id, authority: "guest", channel, sessionToken });
          idToSession.set(id, sessionToken);

          const participantList = room.listParticipants().map((p) => ({
            id: p.id,
            name: p.name,
            type: p.type,
            authority: p.authority ?? "member",
          }));

          res.writeHead(200, { "Content-Type": "application/json" });
          res.end(JSON.stringify({
            sessionToken,
            participantId: id,
            roomName,
            roomId: room.roomId,
            participants: participantList,
            authority: "guest",
          }));
          return;
        }

        // admin or participant — connect as a real participant
        const id = `${participantType}_${randomUUID().slice(0, 8)}`;
        const channel = await room.connect(id, name, { type: participantType, authority });
        const sessionToken = tokens.createSessionToken(id, authority);
        // Stable hive identity keyed on the join handle (survives reconnect).
        const hiveMemberId = memberIdForHandle(name);

        participants.set(sessionToken, { id, name, authority, channel, sessionToken, hiveMemberId });
        idToSession.set(id, sessionToken);

        const participantList = room.listParticipants().map((p) => ({
          id: p.id,
          name: p.name,
          type: p.type,
          authority: p.authority ?? "member",
        }));

        log(`${name} joined (${authority})`);

        res.writeHead(200, { "Content-Type": "application/json" });
        res.end(JSON.stringify({
          sessionToken,
          participantId: id,
          roomName,
          roomId: room.roomId,
          participants: participantList,
          authority,
        }));
        return;
      }

      // ── All remaining POST endpoints require a session token ────────────

      const sessionToken = String(body.token ?? "");
      const session = getSession(sessionToken);

      // ── POST /message ───────────────────────────────────────────────────
      if (url.pathname === "/message") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot send messages");
        const content = String(body.content ?? "");
        const replyTo = body.replyTo ? String(body.replyTo) : undefined;
        if (!content) return jsonError(res, 400, "Empty message");

        const p = participants.get(sessionToken);
        if (!p) return jsonError(res, 403, "Not a participant");

        const msg = await p.channel.sendMessage(content, replyTo);
        jsonOk(res, { messageId: msg.id });
        return;
      }

      // ── POST /event ─────────────────────────────────────────────────────
      if (url.pathname === "/event") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot emit events");
        const event = body.event as RoomEvent | undefined;
        if (!event) return jsonError(res, 400, "Missing event");

        const p = participants.get(sessionToken);
        if (!p) return jsonError(res, 403, "Not a participant");

        await p.channel.emit(event);
        jsonOk(res);
        return;
      }

      // ── POST /hive/intent — "what I'm building now" ─────────────────────
      if (url.pathname === "/hive/intent") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot share intent");
        const text = String(body.text ?? "");
        if (!text) return jsonError(res, 400, "Missing text");
        const intentEvent = hive.shareIntent(hiveMemberOf(session), text, Date.now());
        mirrorTracked(intentEvent);
        jsonOk(res);
        return;
      }

      // ── POST /hive/claim — place an advisory hold on an area ────────────
      if (url.pathname === "/hive/claim" || url.pathname === "/hive/release") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot claim areas");
        const area = String(body.area ?? "").trim();
        if (!area) return jsonError(res, 400, "Missing area");
        const released = url.pathname === "/hive/release";
        mirrorTracked(
          released
            ? hive.releaseClaim(hiveMemberOf(session), area, Date.now())
            : hive.recordClaim(hiveMemberOf(session), area, Date.now()),
        );
        jsonOk(res);
        return;
      }

      // ── Contract negotiation: propose / respond / finalize ─────────────
      if (url.pathname === "/hive/propose") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot propose contracts");
        const name = String(body.name ?? "");
        const newShape = String(body.newShape ?? "");
        if (!name || !newShape) return jsonError(res, 400, "Missing name or newShape");
        const rationale = body.rationale ? String(body.rationale) : undefined;
        const dependsOn = Array.isArray(body.dependsOn) ? body.dependsOn.map(String) : undefined;
        const proposalId = randomUUID();
        mirrorTracked(hive.proposeContract(hiveMemberOf(session), proposalId, name, newShape, rationale, dependsOn, Date.now()));
        jsonOk(res, { proposalId });
        return;
      }
      if (url.pathname === "/hive/respond") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot respond to proposals");
        const proposalId = String(body.proposalId ?? "");
        const v = String(body.verdict ?? "");
        if (!proposalId || (v !== "accepted" && v !== "changes_requested" && v !== "rejected")) {
          return jsonError(res, 400, "Missing proposalId or invalid verdict");
        }
        const reason = body.reason ? String(body.reason) : undefined;
        const counterShape = body.counterShape ? String(body.counterShape) : undefined;
        mirrorTracked(hive.respondToProposal(hiveMemberOf(session), proposalId, v, reason, counterShape, Date.now()));
        jsonOk(res);
        return;
      }
      if (url.pathname === "/hive/finalize") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot finalize proposals");
        const proposalId = String(body.proposalId ?? "");
        if (!proposalId) return jsonError(res, 400, "Missing proposalId");
        const { event, contractEvent, changed } = hive.finalizeProposal(proposalId, Date.now());
        if (contractEvent) mirrorTracked(contractEvent);
        if (changed) mirrorTracked(changed);
        mirrorTracked(event);
        jsonOk(res, { version: contractEvent?.version ?? event.version ?? 0 });
        return;
      }

      // ── POST /hive/decision — record a durable decision / project memory ─
      if (url.pathname === "/hive/decision") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot record decisions");
        const text = String(body.text ?? "");
        if (!text) return jsonError(res, 400, "Missing text");
        const tags = Array.isArray(body.tags) ? body.tags.map(String) : undefined;
        mirrorTracked(hive.recordDecision(hiveMemberOf(session), text, tags, Date.now()));
        jsonOk(res);
        return;
      }

      // ── POST /hive/brief — set/replace the team's PROJECT BRIEF ─────────
      if (url.pathname === "/hive/brief") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot set the brief");
        const text = String(body.text ?? "").slice(0, BRIEF_MAX_LEN);
        if (!text) return jsonError(res, 400, "Missing text");
        mirrorTracked(hive.setBrief(hiveMemberOf(session), text, Date.now()));
        jsonOk(res);
        return;
      }

      // ── POST /hive/verification — record a verification result (loop exit) ─
      if (url.pathname === "/hive/verification") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot record verifications");
        const status = body.status === "pass" || body.status === "fail" || body.status === "partial" ? body.status : null;
        const summary = String(body.summary ?? "").slice(0, VERIFICATION_MAX_LEN);
        if (!status) return jsonError(res, 400, "Missing or invalid status");
        if (!summary) return jsonError(res, 400, "Missing summary");
        const target = body.target ? String(body.target) : undefined;
        const score = typeof body.score === "number" ? body.score : undefined;
        const evidence = body.evidence ? String(body.evidence) : undefined;
        mirrorTracked(
          hive.recordVerification(hiveMemberOf(session), { status, summary, target, score, evidence }, Date.now()),
        );
        jsonOk(res);
        return;
      }

      // ── POST /hive/goal — set/replace the loop's goal (objective + budget) ─
      if (url.pathname === "/hive/goal") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot set goals");
        const text = String(body.text ?? "").slice(0, GOAL_MAX_LEN);
        if (!text) return jsonError(res, 400, "Missing text");
        const goalId = body.goalId ? String(body.goalId) : `goal_${randomUUID().slice(0, 8)}`;
        const maxIterations = typeof body.maxIterations === "number" ? body.maxIterations : undefined;
        const budgetTokens = typeof body.budgetTokens === "number" ? body.budgetTokens : undefined;
        const stopCondition = body.stopCondition ? String(body.stopCondition) : undefined;
        mirrorTracked(
          hive.setGoal(
            hiveMemberOf(session),
            { goalId, text, status: "active", maxIterations, budgetTokens, stopCondition },
            Date.now(),
          ),
        );
        jsonOk(res, { goalId });
        return;
      }

      // ── POST /hive/goal/update — update an existing goal (status/burn) ─────
      if (url.pathname === "/hive/goal/update") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot update goals");
        const goalId = String(body.goalId ?? "");
        if (!goalId) return jsonError(res, 400, "Missing goalId");
        const existing = hive.allGoals().find((g) => g.goalId === goalId);
        if (!existing) {
          jsonOk(res, { found: false });
          return;
        }
        const status =
          body.status === "active" || body.status === "blocked" || body.status === "done" || body.status === "abandoned"
            ? body.status
            : existing.status;
        const iteration = typeof body.iteration === "number" ? body.iteration : existing.iteration;
        const spentTokens = typeof body.spentTokens === "number" ? body.spentTokens : existing.spentTokens;
        mirrorTracked(
          hive.setGoal(
            hiveMemberOf(session),
            {
              goalId,
              text: existing.text,
              status,
              maxIterations: existing.maxIterations,
              iteration,
              budgetTokens: existing.budgetTokens,
              spentTokens,
              stopCondition: existing.stopCondition,
            },
            Date.now(),
          ),
        );
        jsonOk(res, { found: true });
        return;
      }

      // ── POST /hive/plan — set/replace the team's shared plan ────────────
      if (url.pathname === "/hive/plan") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot share plans");
        const rawSteps = Array.isArray(body.steps) ? body.steps : null;
        if (!rawSteps) return jsonError(res, 400, "Missing steps");
        const steps = rawSteps.map((s) => {
          const o = (s ?? {}) as { text?: unknown; status?: unknown };
          const status = o.status === "doing" || o.status === "done" ? o.status : "todo";
          return { text: String(o.text ?? ""), status: status as "todo" | "doing" | "done" };
        });
        mirrorTracked(hive.sharePlan(hiveMemberOf(session), steps, Date.now()));
        jsonOk(res);
        return;
      }

      // ── POST /hive/contracts — declare / advance an interface contract ──
      if (url.pathname === "/hive/contracts") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot declare contracts");
        const name = String(body.name ?? "");
        const shape = String(body.shape ?? "");
        if (!name || !shape) return jsonError(res, 400, "Missing name or shape");
        const dependsOn = Array.isArray(body.dependsOn) ? body.dependsOn.map(String) : undefined;
        // B6: honour the stability flag the client now sends (was dropped here).
        const stability =
          body.stability === "stable" || body.stability === "unstable" || body.stability === "frozen"
            ? body.stability
            : undefined;
        const files = Array.isArray(body.files) ? body.files.map(String) : undefined; // CAP-1
        // G1: the behavioural conformance spec rides along (validated by the reducer's
        // ContractEvent zod when the event is appended; a malformed one is dropped).
        const conformance = parseConformance(body.conformance);
        const result = hive.declareContract(hiveMemberOf(session), name, shape, dependsOn, Date.now(), stability, files, conformance);
        mirrorTracked(result.event);
        if (result.changed) mirrorTracked(result.changed);

        // The gasp moment: when a contract advances, push the alert into
        // teammates' live sessions. We reuse the chat substrate's broadcast —
        // the declaring participant emits the alert as a message, which the
        // forked SSE → tmux bridge already injects into other agents' sessions
        // (engagement modes handle loop prevention). The structured event is
        // also in HiveRoomState for the pull path (get_team_context).
        if (result.changed) {
          const p = participants.get(sessionToken);
          if (p) await p.channel.sendMessage(formatContractChangedPush(result.changed)).catch(() => {});
        }

        jsonOk(res, { version: result.event.version });
        return;
      }

      // ── POST /hive/contract-status — flag stability without redeclaring (B7) ─
      if (url.pathname === "/hive/contract-status") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot set contract status");
        const name = String(body.name ?? "");
        const stability = body.stability;
        if (!name || (stability !== "stable" && stability !== "unstable" && stability !== "frozen")) {
          return jsonError(res, 400, "Missing name or invalid stability");
        }
        mirrorTracked(hive.setContractStatus(hiveMemberOf(session), name, stability, Date.now()));
        jsonOk(res);
        return;
      }

      // ── POST /hive/directive — set the standing operator directive (B7) ─────
      if (url.pathname === "/hive/directive") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot set directives");
        const text = String(body.text ?? "");
        if (!text) return jsonError(res, 400, "Missing text");
        const targets = Array.isArray(body.targets) ? body.targets.map(String) : undefined;
        mirrorTracked(hive.setDirective(text, hiveMemberOf(session), Date.now(), targets));
        jsonOk(res);
        return;
      }

      // ── POST /hive/directive/clear — clear the standing directive (B7) ──────
      if (url.pathname === "/hive/directive/clear") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot clear directives");
        mirrorTracked(hive.clearDirective(hiveMemberOf(session), Date.now()));
        jsonOk(res);
        return;
      }

      // ── POST /hive/depend-on — register a consumer edge (CAP-3) ─────────────
      if (url.pathname === "/hive/depend-on") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot register dependencies");
        const name = String(body.name ?? "").trim();
        if (!name) return jsonError(res, 400, "Missing name");
        mirrorTracked(hive.recordConsumer(hiveMemberOf(session), name, Date.now()));
        jsonOk(res);
        return;
      }

      // ── POST /hive/ruleset — set the team ruleset (CAP-5) ───────────────────
      if (url.pathname === "/hive/ruleset") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot set the ruleset");
        const text = String(body.text ?? "");
        if (!text) return jsonError(res, 400, "Missing text");
        const event = hive.setRuleset(text, hiveMemberOf(session), Date.now());
        mirrorTracked(event);
        jsonOk(res, { version: event.version });
        return;
      }

      // ── POST /hive/contract-history — a contract's version arc (CAP-4) ──────
      if (url.pathname === "/hive/contract-history") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        const name = String(body.name ?? "").trim();
        if (!name) return jsonError(res, 400, "Missing name");
        jsonOk(res, { versions: hive.contractHistory(name) });
        return;
      }

      // ── POST /hive/message — directed agent-to-agent message ────────────
      if (url.pathname === "/hive/message") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot send messages");
        const text = String(body.text ?? "");
        const to = String(body.to ?? "").trim();
        if (!text) return jsonError(res, 400, "Missing text");

        const sender = participants.get(sessionToken);
        const fromName = sender?.name ?? session.id;
        const fromMember = hiveMemberOf(session);
        const roster = [...participants.values()].map((p) => p.name);

        // Resolve the recipient: broadcast keywords, else case-insensitive name
        // (or raw id) match. Same-name teammates all receive it.
        const broadcast = /^(all|everyone|team|the team)$/i.test(to);
        const matches = broadcast
          ? []
          : [...participants.values()].filter(
              (p) => p.name.toLowerCase() === to.toLowerCase() || p.id === to,
            );

        if (!broadcast && matches.length === 0) {
          jsonOk(res, { to: null, roster }); // unknown teammate — the MCP handler shows the roster
          return;
        }

        // Route to the recipient's STABLE hive member id (not the ephemeral
        // connection id), so a directed message survives the recipient
        // reconnecting and is delivered via their get_team_context cursor.
        const recipientIds = broadcast ? ["all"] : [...new Set(matches.map((m) => m.hiveMemberId))];
        for (const rid of recipientIds) {
          mirrorTracked(hive.sendMessage(fromMember, rid, text, Date.now(), fromName));
        }

        // Live mid-session injection: broadcasts go to every live session via the
        // chat substrate. Directed messages are delivered through get_team_context
        // (the prompt hook injects them pre-prompt), so we do not broadcast those.
        if (broadcast && sender) {
          await sender.channel
            .sendMessage(formatMessagePush({ type: "message", from: fromMember, fromName, to: "all", text, ts: Date.now() }))
            .catch(() => {});
        }

        const resolvedName = broadcast ? "everyone" : matches[0]!.name;
        jsonOk(res, { to: resolvedName, roster });
        return;
      }

      // ── POST /set-mode ──────────────────────────────────────────────────
      if (url.pathname === "/set-mode") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        const targetId = body.participantId ? String(body.participantId) : session.id;
        const mode = String(body.mode ?? "");
        if (!mode) return jsonError(res, 400, "Missing mode");

        // Setting someone else's mode requires admin
        if (targetId !== session.id && session.authority !== "admin") {
          return jsonError(res, 403, "Only admins can change other participants' modes");
        }

        // Emit mode_changed activity event
        const p = participants.get(sessionToken);
        if (!p) return jsonError(res, 403, "Not a participant");

        await p.channel.emit(createEvent<ActivityEvent>({
          type: "Activity",
          category: "ACTIVITY",
          room_id: room.roomId,
          participant_id: targetId,
          action: "mode_changed",
          detail: { mode },
        }));

        jsonOk(res);
        return;
      }

      // ── POST /set-authority ──────────────────────────────────────────────
      if (url.pathname === "/set-authority") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority !== "admin") return jsonError(res, 403, "Only admins can change authority");
        const targetId = String(body.participantId ?? "");
        const newAuthority = String(body.authority ?? "") as AuthorityLevel;
        if (!targetId) return jsonError(res, 400, "Missing participantId");
        if (!["admin", "member", "guest"].includes(newAuthority)) {
          return jsonError(res, 400, "Invalid authority. Must be admin, member, or guest.");
        }
        if (targetId === session.id) return jsonError(res, 400, "Cannot change own authority");

        // Update all three places: ConnectedParticipant, TokenManager session, Room participant
        const targetSession = idToSession.get(targetId);
        if (!targetSession) return jsonError(res, 404, "Participant not found");
        const target = participants.get(targetSession);
        if (!target) return jsonError(res, 404, "Participant not found");

        target.authority = newAuthority;
        tokens.updateSessionAuthority(targetSession, newAuthority);
        room.setParticipantAuthority(targetId, newAuthority);

        // Emit AuthorityChanged event
        const adminP = participants.get(sessionToken);
        const targetParticipant = room.listParticipants().find(p => p.id === targetId);
        if (adminP && targetParticipant) {
          await adminP.channel.emit(createEvent<AuthorityChangedEvent>({
            type: "AuthorityChanged",
            category: "PRESENCE",
            room_id: room.roomId,
            participant_id: targetId,
            participant: targetParticipant,
            new_authority: newAuthority,
            changed_by: adminP.name,
          }));
        }

        log(`${target.name} authority → ${newAuthority}`);
        jsonOk(res);
        return;
      }

      // ── POST /kick ──────────────────────────────────────────────────────
      if (url.pathname === "/kick") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority !== "admin") return jsonError(res, 403, "Only admins can kick");
        const targetId = String(body.participantId ?? "");
        if (!targetId) return jsonError(res, 400, "Missing participantId");

        // Find and disconnect the target
        const targetSession = idToSession.get(targetId);
        if (targetSession) {
          const target = participants.get(targetSession) ?? guests.get(targetSession);
          if (target) {
            // Emit ParticipantKicked before disconnect so all participants see it
            const adminP = participants.get(sessionToken);
            const targetParticipant = room.listParticipants().find(p => p.id === targetId);
            if (adminP && targetParticipant) {
              await adminP.channel.emit(createEvent<ParticipantKickedEvent>({
                type: "ParticipantKicked",
                category: "PRESENCE",
                room_id: room.roomId,
                participant_id: targetId,
                participant: targetParticipant,
                kicked_by: adminP.name,
              }));
            }
            // Silent disconnect — the kicked event replaces ParticipantLeft
            await target.channel.disconnect(true);
            participants.delete(targetSession);
            guests.delete(targetSession);
            idToSession.delete(targetId);
            tokens.revokeSessionToken(targetSession);
            // Close SSE connection
            const sse = sseConnections.get(targetId);
            if (sse) {
              sse.end();
              sseConnections.delete(targetId);
            }
            log(`kicked ${targetId}`);
          }
        }

        jsonOk(res);
        return;
      }

      // ── POST /share ─────────────────────────────────────────────────────
      if (url.pathname === "/share") {
        if (!session) return jsonError(res, 401, "Invalid session token");
        if (session.authority === "guest") return jsonError(res, 403, "Guests cannot create share links");

        const targetAuthority = (body.authority as AuthorityLevel) ?? undefined;

        const links: Record<string, string> = {};

        if (targetAuthority) {
          // Generate a specific link
          const token = tokens.generateShareToken(session.authority, targetAuthority);
          if (!token) return jsonError(res, 403, `Cannot generate ${targetAuthority} link`);
          links[targetAuthority] = buildShareUrl(publicUrl, token);
        } else {
          // Generate all links the caller can create
          const tiers: AuthorityLevel[] = ["admin", "member", "guest"];
          for (const tier of tiers) {
            const token = tokens.generateShareToken(session.authority, tier);
            if (token) links[tier] = buildShareUrl(publicUrl, token);
          }
        }

        res.writeHead(200, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ links }));
        return;
      }

      // ── POST /disconnect ────────────────────────────────────────────────
      if (url.pathname === "/disconnect") {
        // Accept either session token or legacy participantId/agentId
        const token = String(body.token ?? "");
        const legacyId = String(body.participantId ?? body.agentId ?? "");

        let targetToken = token;
        if (!targetToken && legacyId) {
          targetToken = idToSession.get(legacyId) ?? "";
        }

        if (targetToken) {
          const p = participants.get(targetToken);
          if (p) {
            await p.channel.disconnect();
            participants.delete(targetToken);
            idToSession.delete(p.id);
            tokens.revokeSessionToken(targetToken);
            const sse = sseConnections.get(p.id);
            if (sse) { sse.end(); sseConnections.delete(p.id); }
            log(`${p.name} disconnected`);
          }

          const g = guests.get(targetToken);
          if (g) {
            await g.channel.disconnect();
            guests.delete(targetToken);
            idToSession.delete(g.id);
            tokens.revokeSessionToken(targetToken);
            const sse = sseConnections.get(g.id);
            if (sse) { sse.end(); sseConnections.delete(g.id); }
          }
        }

        jsonOk(res);
        return;
      }
    }

    res.writeHead(404).end("Not found");
  });

  httpServer.on("error", (err: NodeJS.ErrnoException) => {
    if (err.code === "EADDRINUSE") {
      console.error(`\nPort ${port} is already in use. Another ${BRAND.name} instance may be running.`);
      console.error(`  Kill it:   lsof -ti :${port} | xargs kill`);
      console.error(`  Or use:    ${BRAND.name} --port ${port + 1}\n`);
      process.exit(1);
    }
    throw err;
  });

  await new Promise<void>((resolve) => {
    httpServer.listen(port, "0.0.0.0", () => resolve());
  });

  // Start tunnel if --share
  if (options.share) {
    tunnelProcess = await startTunnel(port);
    if (tunnelProcess) {
      const tunnelUrl = await waitForTunnelUrl(tunnelProcess);
      if (tunnelUrl) {
        publicUrl = tunnelUrl;
      } else {
        // HONESTY over silence: the tunnel never produced a URL (timeout or
        // cloudflared exited). The server still runs, but the printed URLs are
        // local-only — say so instead of letting a teammate paste a dead link.
        // Plain text (NO_COLOR-safe); exit behavior unchanged.
        console.error(
          "\n  Warning: sharing failed — the cloudflared tunnel did not come up in time." +
            "\n  The URLs below are LOCAL-ONLY (same machine / same network)." +
            "\n  Fix: check that cloudflared works (`cloudflared tunnel --url http://localhost:" +
            port +
            "`), then rerun with --share.\n",
        );
      }
    }
    // startTunnel(null) already printed the "requires cloudflared" hint.
  }

  // Generate share tokens on boot
  const adminToken = tokens.generateShareToken("admin", "admin")!;
  const memberToken = tokens.generateShareToken("admin", "member")!;

  if (options.headless) {
    process.stdout.write(JSON.stringify({ serverUrl, publicUrl, roomName, adminToken, memberToken, savePath }) + "\n");
  } else if (!options.quiet) {
    let version = process.env.npm_package_version ?? "";
    if (!version) {
      try {
        const require = createRequire(import.meta.url);
        const pkg = require("../../package.json");
        version = pkg.version ?? "unknown";
      } catch {
        version = "unknown";
      }
    }
    const adminUrl = buildShareUrl(publicUrl, adminToken);
    const joinUrl = buildShareUrl(publicUrl, memberToken);

    console.log(`
  ${BRAND.name} v${version}

  Room:    ${roomName}
  Server:  ${serverUrl}${publicUrl !== serverUrl ? `\n  Tunnel:  ${publicUrl}` : ""}
  Saving:  ${savePath}

  Join:      aethereum join ${joinUrl}
  Admin:     aethereum join ${adminUrl}
  Claude:    aethereum run claude --name MyClaude  →  then tell agent to join: ${joinUrl}
  Codex:     aethereum run codex --name MyCodex   →  then tell agent to join: ${joinUrl}
`);
  }

  // ── Graceful shutdown ──────────────────────────────────────────────────

  const stop = async () => {
    spineSubscriber?.stop();
    // Flush the mirror outbox and persist the final hive snapshot so a clean
    // shutdown loses nothing (crash recovery is covered by the debounced writes).
    await mirror.flush().catch(() => {});
    mirror.stop();
    try {
      const { writeFileSync } = await import("node:fs");
      writeFileSync(
        hiveSidecarPath,
        JSON.stringify({ v: 1, hive: hive.toJSON(), handleToMember: [...handleToMember], mirror: mirror.pending() }),
      );
    } catch {
      // best-effort — never block shutdown on persistence
    }
    await relayChannel?.disconnect().catch(() => {});
    if (tunnelProcess) { tunnelProcess.kill(); tunnelProcess = null; }
    for (const [id, sse] of sseConnections) { sse.end(); sseConnections.delete(id); }
    for (const p of participants.values()) { await p.channel.disconnect().catch(() => {}); }
    for (const g of guests.values()) { await g.channel.disconnect().catch(() => {}); }
    if (httpServer.listening) {
      await new Promise<void>((resolve) => {
        httpServer.close(() => resolve());
        // Terminate lingering keep-alive sockets so close() actually completes.
        httpServer.closeAllConnections?.();
      });
    }
    process.off("SIGINT", shutdown);
    process.off("SIGTERM", shutdown);
  };

  function shutdown(): void {
    log("shutting down...");
    void stop().then(() => process.exit(0));
  }

  process.on("SIGINT", shutdown);
  process.on("SIGTERM", shutdown);

  return { serverUrl, publicUrl, roomName, adminToken, memberToken, stop };
}

// ── Server log ────────────────────────────────────────────────────────────────

function logServer(message: string): void {
  console.log(`  [${formatTimestamp(new Date())}] ${message}`);
}

// ── Cloudflared tunnel ───────────────────────────────────────────────────────

function cloudflaredAvailable(): boolean {
  try {
    execFileSync("which", ["cloudflared"], { stdio: "ignore" });
    return true;
  } catch {
    return false;
  }
}

async function startTunnel(port: number): Promise<ChildProcess | null> {
  if (!cloudflaredAvailable()) {
    console.error("  --share requires cloudflared. Install: brew install cloudflared");
    return null;
  }

  const child = spawn("cloudflared", ["tunnel", "--url", `http://localhost:${port}`], {
    stdio: ["ignore", "ignore", "pipe"],
  });

  child.on("error", () => {
    // cloudflared failed to start
  });

  return child;
}

function waitForTunnelUrl(child: ChildProcess, timeoutMs = 15000): Promise<string | null> {
  return new Promise((resolve) => {
    let resolved = false;
    let buffer = "";

    const timer = setTimeout(() => {
      if (!resolved) { resolved = true; resolve(null); }
    }, timeoutMs);

    child.stderr?.on("data", (chunk: Buffer) => {
      buffer += chunk.toString();
      const match = buffer.match(/https:\/\/[a-z0-9-]+\.trycloudflare\.com/);
      if (match && !resolved) {
        resolved = true;
        clearTimeout(timer);
        resolve(match[0]);
      }
    });

    child.on("exit", () => {
      if (!resolved) { resolved = true; clearTimeout(timer); resolve(null); }
    });
  });
}
